import * as Styled from "../CreateEventPage.styles";

import { AddCircleOutline, EditOutlined } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DatePickerInput,
  FileInput,
  Input,
  MeetingRoom,
  RichTextEditor,
  TimeRangePicker,
} from "../../../components";

import { Room } from "../../../types/room";

const DetailsSection = ({
  formMethods,
  formOptions,
  environment,
  userInfo,
  format,
  roomFinderError,
  roomFinderErrorMessage,
  rooms,
  roomsLoading,
  setShowRoomFinderModal,
  eventDescription,
  eventType,
  placeholderFileName,
  setPlaceholderFileName,
  bypassSendEmail,
}: any) => {
  const theme = useTheme();
  return (
    <Styled.FormSectionContainer>
      <Typography variant="h3"> Details </Typography>
      <Input
        name="title"
        control={formMethods.control}
        label="Event Title"
        required
        placeholder="Enter title..."
        fullWidth
      />
      <Typography
        variant="body2"
        sx={{
          color: theme.colors.primary.slalomDarkBlue,
          marginTop: theme.spacing(2),
        }}
      >
        Event Description *
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.colors.neutral.darkGray,
        }}
      >
        The event description will be added to the Outlook email invite. Use the
        space below to format your text, or paste text to this area from Word or
        Outlook. Currently you cannot add images, but that will be coming soon!
      </Typography>
      <RichTextEditor
        name="description"
        control={formMethods.control}
        setValue={formMethods.setValue}
        required
        label="Event Description"
        defaultValue={eventDescription || ""}
      />

      {/* Date & Time Section */}
      <Styled.InputStack
        direction={{ mobile: "column", desktop: "row" }}
        spacing={theme.spacing(3)}
        width="100%"
      >
        <DatePickerInput
          name="eventDate"
          control={formMethods.control}
          label="Event Date"
          required
        />
        <TimeRangePicker
          startName="startDateTime"
          endName="endDateTime"
          required
          control={formMethods.control}
        />
      </Styled.InputStack>

      {/* Dress Code, Cost, Event Image */}
      <Styled.InputStack
        direction={{ mobile: "column", desktop: "row" }}
        spacing={theme.spacing(3)}
        width="100%"
      >
        <Input
          name="dressCode"
          control={formMethods.control}
          label="Dress Code"
          placeholder="Enter title..."
          select
          selectOptions={formOptions.dressCode}
          sx={{
            width: "100%",
            [theme.breakpoints.up("desktop")]: { width: "33%" },
          }}
        />
        <Input
          name="admissionCost"
          control={formMethods.control}
          label="Cost"
          placeholder="0.00"
          inputProps={{ type: "number" }}
          startAdornment={
            <InputAdornment position="start">
              <Typography variant="body1" style={{ color: "#000000" }}>
                $
              </Typography>
            </InputAdornment>
          }
          sx={{
            width: "100%",
            [theme.breakpoints.up("desktop")]: { width: "33%" },
          }}
        />
        <FileInput
          name="eventImageFile"
          control={formMethods.control}
          label="Event Image"
          placeholder="900x400 .jpg or .png"
          sx={{
            width: "100%",
            [theme.breakpoints.up("desktop")]: { width: "33%" },
          }}
          placeholderFileName={placeholderFileName}
          setPlaceholderFileName={setPlaceholderFileName}
        />
      </Styled.InputStack>
      <Input
        name="eventTags"
        control={formMethods.control}
        label="Event Tag(s)"
        required
        select
        multiple
        selectOptions={formOptions.eventTags}
        fullWidth
      />
      <Styled.InputStack
        direction={{ mobile: "column", desktop: "row" }}
        spacing={theme.spacing(3)}
        width="100%"
      >
        <Input
          name="format"
          control={formMethods.control}
          label="Event Format"
          required
          select
          selectOptions={formOptions.eventFormats}
          sx={{
            width: "100%",
            [theme.breakpoints.up("desktop")]: { width: "33%" },
          }}
        />
        <Input
          name="eventType"
          control={formMethods.control}
          label="Event Type"
          required
          select
          selectOptions={formOptions.eventTypes}
          sx={{
            width: "100%",
            [theme.breakpoints.up("desktop")]: { width: "33%" },
          }}
          readOnly={format === "Virtual"}
        />
        <Input
          name="office"
          control={formMethods.control}
          label="Office"
          required
          select
          selectOptions={formOptions.offices}
          sx={{
            width: "100%",
            [theme.breakpoints.up("desktop")]: { width: "33%" },
          }}
        />
      </Styled.InputStack>
      {format !== "Virtual" && eventType === "Off-site" && (
        <>
          <Typography variant="h3" sx={{ marginTop: theme.spacing(2) }}>
            {" "}
            Location{" "}
          </Typography>
          <Input
            name="location"
            control={formMethods.control}
            label="Location"
            required
            placeholder="Enter details..."
            fullWidth
          />
          <Typography
            variant="body2"
            sx={{
              color: theme.colors.neutral.darkGray,
              marginTop: theme.spacing(1),
            }}
          >
            Note: Our Google Maps auto-fill integration is coming soon, so in
            the meanwhile, please grab a formatted address from Google.
          </Typography>
          <Input
            name="transportationParkingDetails"
            control={formMethods.control}
            label="Transportation & parking details"
            placeholder="Enter details..."
            fullWidth
            multiline
          />
        </>
      )}
      {format !== "Virtual" && eventType === "On-site" && (
        <>
          <Typography
            variant="h3"
            sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}
          >
            Room(s)
          </Typography>
          <Typography
            variant="subtitle1"
            style={{
              color: theme.colors.neutral.darkGray,
              marginBottom: theme.spacing(2),
            }}
          >
            Your selected rooms will be booked after publishing this event.
          </Typography>
          <>
            {rooms.length > 0 ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: theme.spacing(2),
                  }}
                >
                  {roomsLoading && <CircularProgress size={24} />}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      maxHeight: 280,
                      overflowY: "auto",
                      width: "100%",
                    }}
                  >
                    {rooms.map((room: Room) => (
                      <Box key={room.name} sx={{ marginBottom: 1 }}>
                        <MeetingRoom
                          room={room}
                          actionText="Change Room"
                          onClick={() => setShowRoomFinderModal(true)}
                          border={`1px solid ${theme.colors.neutral.softGray}`}
                          bypassShowAction
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  style={{ cursor: "pointer", marginTop: theme.spacing(1) }}
                  onClick={() => setShowRoomFinderModal(true)}
                >
                  <EditOutlined
                    sx={{
                      color: theme.colors.primary.slalomBlue,
                      position: "relative",
                      top: "2px",
                    }}
                  />
                  <Typography
                    variant="body2"
                    style={{
                      color: theme.colors.primary.slalomBlue,
                      textDecoration: "underline",
                    }}
                  >
                    Edit rooms
                  </Typography>
                </Stack>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: theme.spacing(2),
                  marginTop: theme.spacing(2),
                }}
              >
                {roomsLoading && <CircularProgress size={24} />}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowRoomFinderModal(true)}
                >
                  <Typography
                    variant="body2"
                    style={{
                      color: theme.colors.primary.slalomBlue,
                      textDecoration: "underline",
                    }}
                  >
                    Add room(s)
                  </Typography>
                  <AddCircleOutline
                    sx={{
                      color: theme.colors.primary.slalomBlue,
                      position: "relative",
                      top: "2px",
                    }}
                  />
                </Stack>
              </Box>
            )}
          </>
          {roomFinderError && (
            <Typography
              variant="body2"
              sx={{
                marginTop: theme.spacing(2),
                color: theme.colors.action.failure,
              }}
            >
              {roomFinderErrorMessage}
            </Typography>
          )}
        </>
      )}
      <Typography
        variant="h3"
        sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(1) }}
      >
        Email Invitees
      </Typography>
      {!bypassSendEmail && (
        <>
          <Typography
            variant="subtitle1"
            style={{
              color: theme.colors.neutral.darkGray,
              marginBottom: theme.spacing(2),
            }}
          >
            Select the distribution list(s) you would like to invite to this
            event.
          </Typography>
          <Input
            name="recipientList"
            control={formMethods.control}
            label="Email Invitees"
            required={!bypassSendEmail}
            select
            multiple
            selectOptions={[
              ...(environment !== "prod"
                ? [
                    {
                      label: `Myself (${userInfo.email})`,
                      value: userInfo.email,
                    },
                  ]
                : []),
              ...formOptions.distributionList
                .filter((item: any) =>
                  environment !== "prod" ? !item.prod : item.prod
                )
                .map((item: any) => ({
                  label: item.label,
                  value: item.value,
                })),
            ]}
            removeMarginTop
            fullWidth
          />
        </>
      )}
    </Styled.FormSectionContainer>
  );
};

export default DetailsSection;
